import { Chart as ChartJS, ChartDataset, ChartOptions, LegendOptions, TooltipItem, defaults } from 'chart.js';
import { SpotInterface } from 'types';
import { SelectOption } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import {
    revenueRentalType,
    RevenueRentalType,
    RevenueByMonth,
    RevenueReportByRentalType,
    YoYRevenue,
    ReportDataType,
    PacingReport,
    RevenueByDay,
} from 'types/TransactionReport';
import { compactCurrency, convertCents, currencyWithoutCents } from 'utils/currency';
import {
    getMonthAndDateFromString,
    getDayMonthYearFromString,
    getMonthYearFromString,
    getFullMonthYearFromString,
} from 'utils/date';

// Shared
export const PRIMARY_COLOR = '#ff6712';
export const GUARANTEE_COLOR = '#A4DED3';
export const GUARANTEE_LINE_COLOR = '#0A9183';

export enum ChartId {
    REVENUE_CHART = 'revenue-chart',
    RENTAL_TYPE_CHART = 'rental-type-chart',
    HISTORICAL_CHART = 'historical-chart',
    PACING_CHART = 'pacing-chart',
}

export const filteredLegendOptions: Partial<LegendOptions<'bar' | 'line'>> = {
    display: true,
    position: 'bottom',
    labels: {
        ...defaults.plugins.legend?.labels,
        padding: 20,
        boxHeight: 8,
        boxWidth: 8,
        filter(item, data) {
            if (typeof item.datasetIndex !== 'number') return false;
            return data.datasets[item.datasetIndex].data.some((element) => element !== null && element !== 0);
        },
    },
};

// MoM Widget
export enum Timeframe {
    MONTHLY = 'monthly',
    DAILY = 'daily',
    ALL_TIME = 'all_time',
}

export type OnOffOption = 'on' | 'off';

export function onOffValidation(current: string | null): OnOffOption {
    return current && current === 'on' ? current : 'off';
}

export const timeframeOptions: SelectOption[] = [
    {
        value: Timeframe.DAILY,
        label: 'Daily',
    },
    {
        value: Timeframe.MONTHLY,
        label: 'Monthly',
    },
    {
        value: Timeframe.ALL_TIME,
        label: 'All Time',
    },
];
// Fallback start date for the "All Time" timeframe when a spot has no created_at. Safely predates
// any AirGarage spot's revenue history so we never send an invalid/empty start_date to the backend.
export const ALL_TIME_FALLBACK_DATE = '2015-01-01';

// Start date (YYYY-MM-DD) for the "All Time" timeframe: the spot's creation date. created_at is a
// Unix epoch in seconds, so we convert it the same way the rest of the app does.
export function getAllTimeStartDate(spot: SpotInterface): string {
    if (!spot.created_at) return ALL_TIME_FALLBACK_DATE;
    const date = new Date(Number(spot.created_at) * 1000);
    return isNaN(date.getTime()) ? ALL_TIME_FALLBACK_DATE : date.toISOString().split('T')[0];
}

export enum Period {
    SEVEN = '7',
    FOURTEEN = '14',
    THIRTY = '30',
}

export const periodOptions: SelectOption[] = [
    {
        value: Period.SEVEN,
        label: 'Last 7 days',
    },
    {
        value: Period.FOURTEEN,
        label: 'Last 14 days',
    },
    {
        value: Period.THIRTY,
        label: 'Last 30 days',
    },
];

export enum RevenueType {
    GROSS_REVENUE = 'gross_revenue',
    NET_REVENUE = 'net_revenue',
}

export const revenueTypeOptions: SelectOption[] = [
    {
        value: RevenueType.GROSS_REVENUE,
        label: 'Gross Revenue',
    },
    {
        value: RevenueType.NET_REVENUE,
        label: 'Net Revenue',
    },
];

export function getDataForDailyOrMonthlyGraph(
    report: RevenueByMonth | RevenueByDay,
    type: Timeframe,
    allow0values = false
) {
    const grossRevenue: number[] = [];
    const netRevenue: number[] = [];
    // Only ever populated for monthly data.
    const minimumGuarantee: number[] = [];
    const guaranteeGap: number[] = [];
    const labels: string[] = [];
    let suggestedMax = 0;
    // Only relevant when the guarantee overlay is actually shown; kept separate so a guarantee
    // that exceeds a lot's revenue doesn't stretch the Y-axis on views that don't render it.
    let suggestedMaxWithGuarantee = 0;

    for (const entry of report) {
        const { gross_revenue, net_revenue, date } = entry;
        const minimumGuaranteeAmount = 'minimum_guarantee_amount' in entry ? entry.minimum_guarantee_amount : 0;
        if ((gross_revenue && net_revenue) || allow0values) {
            labels.push(type === Timeframe.MONTHLY ? getMonthYearFromString(date) : getMonthAndDateFromString(date));
            grossRevenue.push(convertCents(gross_revenue));
            netRevenue.push(convertCents(net_revenue));
            minimumGuarantee.push(convertCents(minimumGuaranteeAmount));
            guaranteeGap.push(convertCents(Math.max(0, minimumGuaranteeAmount - net_revenue)));
            if (gross_revenue > suggestedMax) suggestedMax = gross_revenue;
            if (net_revenue > suggestedMax) suggestedMax = net_revenue;
            suggestedMaxWithGuarantee = Math.max(suggestedMaxWithGuarantee, suggestedMax, minimumGuaranteeAmount);
        }
    }

    return {
        [RevenueType.GROSS_REVENUE]: grossRevenue,
        [RevenueType.NET_REVENUE]: netRevenue,
        minimumGuarantee,
        guaranteeGap,
        labels,
        suggestedMax: Math.round((suggestedMax / 100) * 1.1),
        suggestedMaxWithGuarantee: Math.round((suggestedMaxWithGuarantee / 100) * 1.1),
    };
}

export function getStartDateFromFilter(period: string) {
    const now = new Date();
    now.setDate(now.getDate() - Number(period));
    return period ? now.toISOString().split('T')[0] : '';
}

export const CURRENT = 'current';
export const GAP = 'gap';

export const sharedGraphOptions: (suggestedMax: number) => ChartOptions<'bar'> = (suggestedMax: number) => ({
    interaction: {
        mode: 'index',
        intersect: false,
    },
    scales: {
        y: {
            grid: { display: true, borderDash: [2] },
            ticks: {
                callback(tickValue) {
                    return typeof tickValue === 'number' ? compactCurrency.format(tickValue) : '';
                },
                maxTicksLimit: 4,
            },
            suggestedMax,
        },
    },
    plugins: {
        tooltip: {
            callbacks: {
                label(tooltipItem: TooltipItem<'bar'>): string | string[] {
                    return typeof tooltipItem.raw === 'number'
                        ? currencyWithoutCents.format(tooltipItem.raw)
                        : tooltipItem.formattedValue;
                },
                footer(tooltipItems: TooltipItem<'bar'>[]) {
                    const { data, label } = tooltipItems[0].dataset;
                    const isInprogress = label === CURRENT && tooltipItems[0].dataIndex === data.length - 1;
                    return isInprogress ? 'In progress' : '';
                },
            },
            footerAlign: 'center',
        },
        datalabels: {
            display: 'auto',
            anchor: 'end',
            align: 'top',
            formatter: (value) => {
                return typeof value === 'number' ? `${compactCurrency.format(value)}` : '';
            },
            font: {
                size: 10,
                weight: 'bold',
            },
            color: 'black',
        },
    },
});

export function getImgPattern(chart: ChartJS<'bar' | 'line', number[], unknown> | null, img: HTMLImageElement) {
    if (!chart) return;
    const ctx = chart.ctx;
    return ctx.createPattern(img, 'repeat');
}

// Revenue by Rental Type Widget
const rentalTypesValueToLabel: Record<RevenueRentalType, string> = {
    [revenueRentalType.HOURLY]: 'Hourly',
    [revenueRentalType.DAILY]: 'Daily',
    [revenueRentalType.FLAT_RATE]: 'Flat Rate',
    [revenueRentalType.MONTHLY]: 'Monthly',
    [revenueRentalType.ENTERPRISE]: 'Enterprise',
    [revenueRentalType.BULK_RENTAL]: 'Tenants',
    [revenueRentalType.TICKET]: 'Violations',
    [revenueRentalType.ADJUSTMENT]: 'Adjustments',
};

export const rentalTypeOptions: SelectOption[] = [{ value: '', label: 'All' }].concat(
    Object.keys(rentalTypesValueToLabel).map((key: string) => {
        return {
            value: key,
            label: rentalTypesValueToLabel[key as RevenueRentalType],
        };
    })
);

// Whether this spot shares ticket revenue with the owner. The report only carries a
// `ticket` bucket for those spots — it's dropped server-side otherwise — so its presence
// is the gate for every ticket-revenue affordance on the Revenue page.
export function reportHasTicketRevenue(report: RevenueReportByRentalType | null): boolean {
    return !!report?.some((entry) => entry[revenueRentalType.TICKET] !== undefined);
}

// The rental types an owner is allowed to see broken out. Tickets are only one of them when
// the spot shares ticket revenue with the owner — otherwise the "Violations" series is
// neither returned by the report nor something the owner has any claim to, so it must not
// appear in the graph, the filter dropdown, or any export.
export function getSelectableRentalTypes(hasTicketRevenue: boolean): RevenueRentalType[] {
    const allRentalTypes = Object.values(revenueRentalType) as RevenueRentalType[];
    return hasTicketRevenue
        ? allRentalTypes
        : allRentalTypes.filter((rentalType) => rentalType !== revenueRentalType.TICKET);
}

export function getRentalTypeOptions(hasTicketRevenue: boolean): SelectOption[] {
    if (hasTicketRevenue) return rentalTypeOptions;
    return rentalTypeOptions.filter((option) => option.value !== revenueRentalType.TICKET);
}

// Resolves the comma-separated URL filter into the rental types actually rendered. A filter
// that selects nothing selectable — `?rentalType=ticket` on a spot that doesn't share ticket
// revenue — means "all", the same as no filter. Every consumer must agree on this, or the
// graph/table shows one thing while the dropdown and PDF header claim another.
export function resolveSelectedRentalTypes(rentalTypeFilter: string, hasTicketRevenue: boolean): RevenueRentalType[] {
    const selectable = getSelectableRentalTypes(hasTicketRevenue);
    const selected = rentalTypeFilter
        .split(',')
        .filter((rentalType): rentalType is RevenueRentalType => selectable.includes(rentalType as RevenueRentalType));
    return selected.length ? selected : selectable;
}

export function getDataForByRentalTypeGraph(report: RevenueReportByRentalType) {
    const hourlyRevenue: number[] = [];
    const dailyRevenue: number[] = [];
    const monthlyRevenue: number[] = [];
    const flatRevenue: number[] = [];
    const enterpriseRevenue: number[] = [];
    const bulkRentalRevenue: number[] = [];
    const ticketRevenue: number[] = [];
    const adjustmentRevenue: number[] = [];

    const labels: string[] = [];

    for (const { date, hourly, daily, monthly, flat_rate, enterprise, bulk_rental, ticket, adjustment } of report) {
        // The ticket ("Violations") series is only returned for spots that share ticket
        // revenue with the owner; guard against it being absent so the graph still
        // renders. This is a gross chart, so use the gross ticket amount (not the
        // owner's net share) to stay consistent with the other series.
        const ticketGross = ticket?.gross_revenue ?? 0;
        // The adjustment bucket can be missing while an older backend is still serving
        // the previous response shape, so treat its absence as 0 too.
        const adjustmentGross = adjustment?.gross_revenue ?? 0;
        // Only add data if at least one type has revenue
        if (
            hourly.gross_revenue ||
            daily.gross_revenue ||
            monthly.gross_revenue ||
            flat_rate.gross_revenue ||
            enterprise.gross_revenue ||
            bulk_rental.gross_revenue ||
            ticketGross ||
            adjustmentGross
        ) {
            labels.push(getMonthYearFromString(date));
            hourlyRevenue.push(convertCents(hourly.gross_revenue));
            dailyRevenue.push(convertCents(daily.gross_revenue));
            monthlyRevenue.push(convertCents(monthly.gross_revenue));
            flatRevenue.push(convertCents(flat_rate.gross_revenue));
            enterpriseRevenue.push(convertCents(enterprise.gross_revenue));
            bulkRentalRevenue.push(convertCents(bulk_rental.gross_revenue));
            ticketRevenue.push(convertCents(ticketGross));
            adjustmentRevenue.push(convertCents(adjustmentGross));
        }
    }

    return {
        labels,
        [revenueRentalType.HOURLY]: {
            data: hourlyRevenue,
            backgroundColor: '#A3DCA9',
            label: rentalTypesValueToLabel[revenueRentalType.HOURLY],
        },
        [revenueRentalType.DAILY]: {
            data: dailyRevenue,
            backgroundColor: '#50ACC6',
            label: rentalTypesValueToLabel[revenueRentalType.DAILY],
        },
        [revenueRentalType.MONTHLY]: {
            data: monthlyRevenue,
            backgroundColor: '#6F5CDC',
            label: rentalTypesValueToLabel[revenueRentalType.MONTHLY],
        },
        [revenueRentalType.FLAT_RATE]: {
            data: flatRevenue,
            backgroundColor: '#079183',
            label: rentalTypesValueToLabel[revenueRentalType.FLAT_RATE],
        },
        [revenueRentalType.ENTERPRISE]: {
            data: enterpriseRevenue,
            backgroundColor: '#004865',
            label: rentalTypesValueToLabel[revenueRentalType.ENTERPRISE],
        },
        [revenueRentalType.BULK_RENTAL]: {
            data: bulkRentalRevenue,
            backgroundColor: '#FF6712',
            label: rentalTypesValueToLabel[revenueRentalType.BULK_RENTAL],
        },
        [revenueRentalType.TICKET]: {
            data: ticketRevenue,
            backgroundColor: '#EABE3F',
            label: rentalTypesValueToLabel[revenueRentalType.TICKET],
        },
        [revenueRentalType.ADJUSTMENT]: {
            data: adjustmentRevenue,
            backgroundColor: '#8D8D86',
            label: rentalTypesValueToLabel[revenueRentalType.ADJUSTMENT],
        },
    };
}

export function calculateRevenuePercentage(data: number[], value: number) {
    const total = data.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return total ? Math.round((value * 100) / total) : 0;
}

//YoY Revenue Widget
export function getYoYRevenueGraph({ reports }: YoYRevenue) {
    const grossRevenue: (number | null)[] = [];
    const netRevenue: (number | null)[] = [];
    const historicalGrossRevenue: (number | null)[] = [];
    const historicalNetRevenue: (number | null)[] = [];
    const labels: string[] = [];
    let suggestedMax = 0;

    for (const { date, data_type, gross_revenue, net_revenue } of reports) {
        labels.unshift(getMonthYearFromString(date));
        if (gross_revenue > suggestedMax) suggestedMax = gross_revenue;
        if (net_revenue > suggestedMax) suggestedMax = net_revenue;

        switch (data_type) {
            case ReportDataType.TRANSACTIONS_V1:
            case ReportDataType.TRANSACTIONS_V2:
                grossRevenue.unshift(convertCents(gross_revenue));
                netRevenue.unshift(convertCents(net_revenue));
                historicalGrossRevenue.unshift(null);
                historicalNetRevenue.unshift(null);
                break;
            case ReportDataType.HISTORICAL:
                grossRevenue.unshift(null);
                netRevenue.unshift(null);
                historicalGrossRevenue.unshift(convertCents(gross_revenue));
                historicalNetRevenue.unshift(convertCents(net_revenue));
        }
    }

    return {
        [RevenueType.GROSS_REVENUE]: { airGarage: grossRevenue, historical: historicalGrossRevenue },
        [RevenueType.NET_REVENUE]: { airGarage: netRevenue, historical: historicalNetRevenue },
        labels,
        suggestedMax: Math.round((suggestedMax / 100) * 1.1),
    };
}

// Pacing Widget
const COLORS = ['#FF6712', '#50ACC6', '#004865'];

const commonDatasetOptions = {
    pointStyle: 'rectRounded',
    fill: false,
    pointRadius: 1,
    pointHoverBorderColor: '#fff',
    pointBorderColor: 'transparent',
};

export function getPacingGraph(report: PacingReport): { datasets: Array<ChartDataset<'line'>>; labels: number[] } {
    const currentMonthData: number[] = [];
    const prevMonthData: number[] = [];
    const currMontLastYearData: number[] = [];
    const labels: number[] = [];

    report.current_month.forEach((entry, i) => {
        currentMonthData.push(convertCents(entry.cumulative_gross_revenue));
        if (!labels[i]) labels[i] = getDayMonthYearFromString(entry.date).day;
    });

    report.previous_month.forEach((entry, i) => {
        prevMonthData.push(convertCents(entry.cumulative_gross_revenue));
        if (!labels[i]) labels[i] = getDayMonthYearFromString(entry.date).day;
    });

    report.current_month_last_year.forEach((entry, i) => {
        currMontLastYearData.push(convertCents(entry.cumulative_gross_revenue));
        if (!labels[i]) labels[i] = getDayMonthYearFromString(entry.date).day;
    });

    return {
        datasets: [
            {
                data: currentMonthData,
                borderColor: COLORS[0],
                backgroundColor: COLORS[0],
                pointBackgroundColor: COLORS[0],
                label: getFullMonthYearFromString(report.current_month[0]?.date),
                ...commonDatasetOptions,
            },
            {
                data: prevMonthData,
                borderColor: COLORS[1],
                backgroundColor: COLORS[1],
                pointBackgroundColor: COLORS[1],

                label: getFullMonthYearFromString(report.previous_month[0]?.date),
                ...commonDatasetOptions,
            },
            {
                data: currMontLastYearData,
                borderColor: COLORS[2],
                backgroundColor: COLORS[2],
                pointBackgroundColor: COLORS[2],
                label: getFullMonthYearFromString(report.current_month_last_year[0]?.date),
                ...commonDatasetOptions,
            },
        ],
        labels,
    };
}
